import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, ChevronRight, Sparkles, TrendingUp, Users, Target, Palette, Globe, Megaphone } from 'lucide-react';

// Simple Reveal on Scroll
const RevealOnScroll = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const services = [
  {
    id: 'brand-strategy',
    icon: Target,
    image: '/3d-brand-strategy.png',
    title: 'Brand Strategy',
    tagline: 'Defining Your Brand DNA',
    shortDesc: 'Positioning, voice & communication frameworks that set you apart.',
    description: 'We help brands define their positioning, brand voice and communication frameworks. From brand audits to comprehensive strategy development, we ensure your brand stands out in a crowded marketplace.',
    features: [
      'Brand positioning and differentiation',
      'Brand voice and messaging development',
      'Communication framework creation',
      'Brand audit and competitive analysis',
      'Brand guidelines and style guides'
    ],
    color: 'from-violet-500 to-purple-600'
  },
  {
    id: 'social-media',
    icon: Users,
    image: '/3d-social-media-mgmt.png',
    title: 'Social Media Management',
    tagline: 'Building Digital Communities',
    shortDesc: 'Content planning, platform management and audience engagement.',
    description: 'Content planning, platform management and audience engagement that drives real results. We manage your social presence across all platforms to build meaningful connections with your audience.',
    features: [
      'Content planning and calendar management',
      'Platform-specific strategy development',
      'Community management and engagement',
      'Social media analytics and reporting',
      'Influencer collaboration and partnerships'
    ],
    color: 'from-pink-500 to-rose-600'
  },
  {
    id: 'content-production',
    icon: Sparkles,
    image: '/3d-content-production.png',
    title: 'Content Production',
    tagline: 'Stories That Captivate',
    shortDesc: 'Photography, video production and digital content creation.',
    description: 'Photography, video production and digital content creation that brings your brand to life. Our creative team produces high-quality content that resonates with your target audience.',
    features: [
      'Professional photography services',
      'Video production and editing',
      'Motion graphics and animation',
      'Podcast and audio content',
      'Drone and aerial photography'
    ],
    color: 'from-amber-500 to-orange-600'
  },
  {
    id: 'creative-design',
    icon: Palette,
    image: '/3d-creative-design.png',
    title: 'Creative Design',
    tagline: 'Visual Excellence',
    shortDesc: 'Visual identity, brand design and marketing collateral.',
    description: 'Visual identity, brand design and marketing collateral that makes a lasting impression. We create designs that not only look stunning but also communicate your brand message effectively.',
    features: [
      'Logo design and visual identity',
      'Marketing collateral design',
      'Packaging and product design',
      'Social media graphics',
      'Presentation and pitch deck design'
    ],
    color: 'from-emerald-500 to-teal-600'
  },
  {
    id: 'website-design',
    icon: Globe,
    image: '/3d-website-design.png',
    title: 'Website Design & Development',
    tagline: 'Digital Experiences That Convert',
    shortDesc: 'High-performance websites designed for user experience.',
    description: 'High-performance websites designed for user experience and brand storytelling. We build websites that are not just beautiful but also optimized for conversions and search engines.',
    features: [
      'Custom website design and development',
      'E-commerce solutions',
      'Mobile-responsive design',
      'SEO optimization',
      'Website maintenance and support'
    ],
    color: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'performance-marketing',
    icon: Megaphone,
    image: '/3d-performance-marketing.png',
    title: 'Performance Marketing & Ads',
    tagline: 'Results-Driven Campaigns',
    shortDesc: 'Integrated marketing campaigns that deliver measurable results.',
    description: 'Conceptualisation and execution of integrated marketing campaigns that deliver measurable results. From Meta Ads to Google Ads, we ensure every rupee spent drives real business growth.',
    features: [
      'Meta Ads (Facebook & Instagram)',
      'Google Ads (Search, Display, Shopping)',
      'Campaign strategy and planning',
      'A/B testing and optimization',
      'ROI tracking and reporting'
    ],
    color: 'from-red-500 to-pink-600'
  }
];

// Interactive Service Card with Hover Expand
const ServiceCard = ({ service, index, isActive, onHover }) => {
  const Icon = service.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onMouseEnter={() => onHover(service.id)}
      onMouseLeave={() => onHover(null)}
      className={`relative cursor-pointer transition-all duration-500 ${
        isActive ? 'z-20' : 'z-10'
      }`}
    >
      <motion.div
        animate={{
          scale: isActive ? 1.05 : 1,
        }}
        transition={{ duration: 0.3 }}
        className={`relative bg-white rounded-3xl overflow-hidden border-2 transition-all duration-300 ${
          isActive ? 'border-brand-blue shadow-2xl shadow-brand-blue/20' : 'border-gray-100 hover:border-gray-200'
        }`}
      >
        {/* Gradient Background on Hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 transition-opacity duration-500 ${isActive ? 'opacity-5' : ''}`} />
        
        {/* Content */}
        <div className="relative p-8">
          {/* Top Section */}
          <div className="flex items-start justify-between mb-6">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <motion.div
              animate={{ rotate: isActive ? 45 : 0 }}
              transition={{ duration: 0.3 }}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                isActive ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-400'
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.div>
          </div>

          {/* 3D Image */}
          <motion.div 
            className="mb-6 flex justify-center"
            animate={{ 
              y: isActive ? -10 : 0,
              scale: isActive ? 1.1 : 1
            }}
            transition={{ duration: 0.4 }}
          >
            <img 
              src={service.image} 
              alt={service.title}
              className="w-32 h-32 object-contain drop-shadow-xl"
            />
          </motion.div>

          {/* Text Content */}
          <div className="text-center">
            <span className={`text-xs font-bold uppercase tracking-wider bg-gradient-to-r ${service.color} bg-clip-text text-transparent mb-2 block`}>
              {service.tagline}
            </span>
            <h3 className="font-outfit text-xl font-bold text-gray-900 mb-3">
              {service.title}
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              {service.shortDesc}
            </p>
          </div>

          {/* Expanded Content */}
          <AnimatePresence>
            {isActive && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-6 pt-6 border-t border-gray-100"
              >
                <ul className="space-y-2">
                  {service.features.slice(0, 3).map((feature, idx) => (
                    <motion.li 
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center text-sm text-gray-600"
                    >
                      <CheckCircle2 className={`w-4 h-4 mr-2 flex-shrink-0 bg-gradient-to-r ${service.color} bg-clip-text text-transparent`} style={{ color: 'currentColor' }} />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Full-width Service Detail Section
const ServiceDetailSection = ({ service, index, isReversed }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.8 }}
      className={`py-20 ${index % 2 === 0 ? 'bg-gradient-to-b from-white to-gray-50' : 'bg-gradient-to-b from-gray-50 to-white'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
          {/* Image Side */}
          <motion.div 
            initial={{ opacity: 0, x: isReversed ? 50 : -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isReversed ? 50 : -50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`relative ${isReversed ? 'lg:order-2' : ''}`}
          >
            {/* Background Decorations */}
            <div className={`absolute -inset-4 bg-gradient-to-br ${service.color} opacity-10 rounded-3xl blur-2xl`} />
            
            {/* Large Number */}
            <div className="absolute -top-8 -left-4 font-outfit text-[180px] font-bold text-gray-100 select-none -z-10 opacity-50">
              {String(index + 1).padStart(2, '0')}
            </div>
            
            {/* 3D Image */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10"
            >
              <img 
                src={service.image} 
                alt={service.title}
                className="w-full max-w-md mx-auto drop-shadow-2xl"
              />
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div 
            initial={{ opacity: 0, x: isReversed ? -50 : 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: isReversed ? -50 : 50 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className={isReversed ? 'lg:order-1' : ''}
          >
            {/* Icon Badge */}
            <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r ${service.color} bg-opacity-10 mb-6`} style={{ background: `linear-gradient(135deg, ${service.color.includes('violet') ? '#8b5cf6' : service.color.includes('pink') ? '#ec4899' : service.color.includes('amber') ? '#f59e0b' : service.color.includes('emerald') ? '#10b981' : service.color.includes('blue') ? '#3b82f6' : '#ef4444'}15, transparent)` }}>
              <Icon className={`w-5 h-5`} style={{ color: service.color.includes('violet') ? '#8b5cf6' : service.color.includes('pink') ? '#ec4899' : service.color.includes('amber') ? '#f59e0b' : service.color.includes('emerald') ? '#10b981' : service.color.includes('blue') ? '#3b82f6' : '#ef4444' }} />
              <span className="text-sm font-semibold text-gray-700">{service.tagline}</span>
            </div>

            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {service.title}
            </h2>
            
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              {service.description}
            </p>

            {/* Features List */}
            <div className="space-y-5 mb-10">
              {service.features.map((feature, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ delay: 0.4 + idx * 0.1 }}
                  className="flex items-center group"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mr-5 flex-shrink-0 group-hover:scale-110 transition-transform shadow-md`}>
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-gray-700 text-base">{feature}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              className={`inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r ${service.color} text-white font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105`}
            >
              Get Started
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const ServicesPage = () => {
  const [activeService, setActiveService] = useState(null);

  return (
    <div className="w-full pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="services-hero">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-blue/20 via-cyan-500/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center max-w-4xl mx-auto">
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 block">Our Services</span>
              <h1 className="font-outfit text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] text-white mb-6">
                What We <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">Create</span>
              </h1>
              <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
                Zenith Marketing offers integrated marketing services designed to support brands across their growth journey.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* Interactive Services Grid */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealOnScroll>
            <div className="text-center mb-16">
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Explore Our Services
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Hover over each service to discover what we can do for your brand
              </p>
            </div>
          </RevealOnScroll>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard
                key={service.id}
                service={service}
                index={index}
                isActive={activeService === service.id}
                onHover={setActiveService}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Service Sections */}
      {services.map((service, index) => (
        <ServiceDetailSection
          key={service.id}
          service={service}
          index={index}
          isReversed={index % 2 !== 0}
        />
      ))}

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden" data-testid="services-cta">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-brand-blue/10 via-cyan-500/10 to-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <RevealOnScroll>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Get <span className="bg-gradient-to-r from-brand-blue to-cyan-400 bg-clip-text text-transparent">Started?</span>
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how our services can help you achieve your goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="services-contact-button"
                className="inline-flex items-center justify-center px-10 py-4 rounded-full bg-gradient-to-r from-brand-blue to-cyan-400 text-white font-semibold hover:from-cyan-400 hover:to-brand-blue transition-all duration-300 shadow-lg shadow-brand-blue/30"
              >
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/case-studies"
                data-testid="services-case-studies-button"
                className="inline-flex items-center justify-center px-10 py-4 rounded-full border-2 border-white/30 text-white font-semibold hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              >
                View Our Work
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
